import {
	CSSProperties,
	FC,
	useCallback,
	useEffect,
	useState
} from 'react'
import { useTranslation } from 'react-i18next'
import {
	Table,
	Button,
	Space,
	Tooltip,
	Spin,
	Empty,
	Card,
	Modal,
	Form,
	Input,
	Select,
	Switch,
	Popconfirm,
	Tag,
	message
} from 'antd'
import {
	ReloadOutlined,
	PlusOutlined,
	EditOutlined,
	DeleteOutlined
} from '@ant-design/icons'
import { LoggingCode } from '../helpers/types/logging'
import { CreateLoggingCodePayload } from '../api/logging'
import { loggingStore } from '../store/logging'

const SOURCE_OPTIONS = [
	{ label: 'Lighting', value: 'Lighting', prefix: 'LIGHT' },
	{ label: 'Consumers', value: 'Consumers', prefix: 'CONS' },
	{ label: 'Heating', value: 'Heating', prefix: 'HEAT' },
	{ label: 'Calorifer', value: 'Calorifer', prefix: 'CALO' },
	{ label: 'Conditioning', value: 'Conditioning', prefix: 'COND' },
	{ label: 'PCH', value: 'PCH', prefix: 'PCH' },
	{
		label: 'PCH Overheat',
		value: 'PCH Overheat',
		prefix: 'PCHOVH'
	},
	{ label: 'Aqualit', value: 'Aqualit', prefix: 'AQLT' },
	{ label: 'Megalith', value: 'Megalith', prefix: 'MGLTH' },
	{ label: 'RNG', value: 'RNG', prefix: 'RNG' },
	{ label: 'Remote', value: 'Remote', prefix: 'REMOTE' },
	{ label: 'App', value: 'App', prefix: 'APP' }
] as const

const getSourcePrefix = (source: string): string =>
	SOURCE_OPTIONS.find((o) => o.value === source)?.prefix ?? ''

const LoggingCodes: FC = () => {
	const { t } = useTranslation()
	const {
		loggingCodes: codes,
		fetchLoggingCodes,
		createLoggingCode,
		updateLoggingCode,
		deleteLoggingCode
	} = loggingStore()
	const [loading, setLoading] = useState(false)
	const [modalOpen, setModalOpen] = useState(false)
	const [editingCode, setEditingCode] =
		useState<LoggingCode | null>(null)
	const [form] = Form.useForm()
	const [sourcePrefix, setSourcePrefix] = useState('')
	const [codeSuffix, setCodeSuffix] = useState('')

	const loadCodes = useCallback(
		async (force = false) => {
			setLoading(true)
			try {
				await fetchLoggingCodes(force)
			} catch {
				message.error(t('ErrorLoadingData'))
			} finally {
				setLoading(false)
			}
		},
		[fetchLoggingCodes, t]
	)

	useEffect(() => {
		void loadCodes()
	}, [loadCodes])

	const openCreate = () => {
		setEditingCode(null)
		form.resetFields()
		form.setFieldsValue({
			isError: false,
			notify: false,
			type: 'info',
			source: undefined
		})
		setSourcePrefix('')
		setCodeSuffix('')
		setModalOpen(true)
	}

	const openEdit = (record: LoggingCode) => {
		setEditingCode(record)
		const prefix = getSourcePrefix(record.source)
		const suffix =
			prefix && record.code.startsWith(prefix)
				? record.code.slice(prefix.length)
				: record.code
		setSourcePrefix(prefix)
		setCodeSuffix(suffix)
		form.setFieldsValue({
			text: record.text,
			type: record.type,
			source: record.source,
			isError: record.isError,
			notify: record.notify
		})
		setModalOpen(true)
	}

	const handleSourceChange = (value: string) => {
		const prefix = getSourcePrefix(value)
		setSourcePrefix(prefix)
	}

	const handleSave = async () => {
		try {
			const values = await form.validateFields()
			const fullCode = sourcePrefix + codeSuffix
			if (!fullCode) {
				message.error(t('CodeRequired'))
				return
			}
			if (editingCode) {
				await updateLoggingCode(editingCode.code, values)
				message.success(t('SavedSuccessfully'))
			} else {
				await createLoggingCode({
					...values,
					code: fullCode
				} as CreateLoggingCodePayload)
				message.success(t('CreatedSuccessfully'))
			}
			setModalOpen(false)
		} catch {}
	}

	const handleDelete = async (code: string) => {
		try {
			await deleteLoggingCode(code)
			message.success(t('DeletedSuccessfully'))
		} catch {
			message.error(t('ErrorDeletingData'))
		}
	}

	const pageStyle: CSSProperties = {
		height: 'calc(100vh - 70px)',
		overflow: 'auto',
		backgroundColor: '#f5f5f5',
		padding: '16px'
	}

	const headerStyle: CSSProperties = {
		marginBottom: '16px',
		backgroundColor: 'white',
		padding: '16px',
		borderRadius: '4px',
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	}

	const tableContainerStyle: CSSProperties = {
		backgroundColor: 'white',
		borderRadius: '4px',
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
	}

	return (
		<div style={pageStyle}>
			<div style={headerStyle}>
				<div>
					<h1 style={{ margin: '0 0 4px 0' }}>
						{t('LoggingCodes')}
					</h1>
					<p
						style={{
							margin: 0,
							color: '#666',
							fontSize: '12px'
						}}>
						{t('Total')}: <strong>{codes.length}</strong>
					</p>
				</div>
				<Space>
					<Tooltip title={t('Refresh')}>
						<Button
							icon={<ReloadOutlined />}
							onClick={() => loadCodes(true)}
							loading={loading}
						/>
					</Tooltip>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={openCreate}>
						{t('AddCode')}
					</Button>
				</Space>
			</div>

			<Card
				style={tableContainerStyle}
				bodyStyle={{ padding: 0 }}>
				<Spin spinning={loading}>
					{codes.length === 0 && !loading ? (
						<Empty
							description={t('NoData')}
							style={{ padding: '40px 0' }}
						/>
					) : (
						<Table<LoggingCode>
							dataSource={codes}
							rowKey="code"
							pagination={{
								pageSize: 20,
								showSizeChanger: true,
								pageSizeOptions: [10, 20, 50, 100],
								showTotal: (total) =>
									`${t('Total')}: ${total}`,
								position: ['bottomCenter']
							}}
							scroll={{ x: 900 }}
							columns={[
								{
									title: t('Code'),
									dataIndex: 'code',
									key: 'code',
									width: 140,
									fixed: 'left',
									render: (code: string) => (
										<span
											style={{
												fontWeight: 600,
												fontFamily:
													'monospace'
											}}>
											{code}
										</span>
									)
								},
								{
									title: t('EventText'),
									dataIndex: 'text',
									key: 'text',
									ellipsis: true
								},
								{
									title: t('Type'),
									dataIndex: 'type',
									key: 'type',
									width: 120,
									render: (type: string) => (
										<Tag
											color={getTypeColor(
												type
											)}>
											{type}
										</Tag>
									),
									filters: Array.from(
										new Set(
											codes.map((c) => c.type)
										)
									).map((t) => ({
										text: t,
										value: t
									})),
									onFilter: (value, record) =>
										record.type === value
								},
								{
									title: t('Source'),
									dataIndex: 'source',
									key: 'source',
									width: 145,
									render: (source: string) =>
										t(source, {
											defaultValue: source
										})
								},
								{
									title: t('IsError'),
									dataIndex: 'isError',
									key: 'isError',
									width: 100,
									align: 'center',
									render: (v: boolean) =>
										v ? (
											<Tag color="red">
												{t('Yes')}
											</Tag>
										) : (
											<Tag>{t('No')}</Tag>
										),
									filters: [
										{
											text: t('Yes'),
											value: true
										},
										{
											text: t('No'),
											value: false
										}
									],
									onFilter: (value, record) =>
										record.isError === value
								},
								{
									title: t('Notify'),
									dataIndex: 'notify',
									key: 'notify',
									width: 100,
									align: 'center',
									render: (v: boolean) =>
										v ? (
											<Tag color="blue">
												{t('Yes')}
											</Tag>
										) : (
											<Tag>{t('No')}</Tag>
										)
								},
								{
									title: t('Actions'),
									key: 'actions',
									width: 120,
									fixed: 'right',
									align: 'center',
									render: (
										_: unknown,
										record: LoggingCode
									) => (
										<Space size="small">
											<Tooltip
												title={t('Edit')}>
												<Button
													size="small"
													icon={
														<EditOutlined />
													}
													onClick={() =>
														openEdit(
															record
														)
													}
												/>
											</Tooltip>
											<Popconfirm
												title={t(
													'ConfirmDelete'
												)}
												onConfirm={() =>
													handleDelete(
														record.code
													)
												}
												okText={t('Yes')}
												cancelText={t('No')}>
												<Tooltip
													title={t(
														'Delete'
													)}>
													<Button
														size="small"
														danger
														icon={
															<DeleteOutlined />
														}
													/>
												</Tooltip>
											</Popconfirm>
										</Space>
									)
								}
							]}
							size="middle"
							bordered={false}
						/>
					)}
				</Spin>
			</Card>

			<Modal
				title={editingCode ? t('EditCode') : t('AddCode')}
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				onOk={handleSave}
				okText={t('Save')}
				cancelText={t('Cancel')}
				destroyOnClose>
				<Form
					form={form}
					layout="vertical"
					style={{ marginTop: 16 }}>
					<Form.Item
						label={t('Source')}
						name="source"
						rules={[{ required: true }]}>
						<Select
							placeholder={t('Source') as string}
							onChange={handleSourceChange}
							disabled={!!editingCode}
							showSearch
							options={SOURCE_OPTIONS.map((o) => ({
								label: `${t(o.label)} (${o.prefix})`,
								value: o.value
							}))}
						/>
					</Form.Item>
					<div style={{ marginBottom: 24 }}>
						<label
							style={{
								display: 'block',
								marginBottom: 8
							}}>
							{t('Code')}{' '}
							<span style={{ color: '#ff4d4f' }}>
								*
							</span>
						</label>
						<Input
							addonBefore={
								<span
									style={{
										fontFamily: 'monospace',
										fontWeight: 600
									}}>
									{sourcePrefix || '...'}
								</span>
							}
							value={codeSuffix}
							onChange={(e) =>
								setCodeSuffix(
									e.target.value.toUpperCase()
								)
							}
							placeholder="e.g. ERR1"
							disabled={!!editingCode}
							style={{ fontFamily: 'monospace' }}
						/>
						{sourcePrefix && codeSuffix && (
							<div
								style={{
									marginTop: 4,
									fontSize: 12,
									color: '#888'
								}}>
								{t('Code')}:{' '}
								<strong
									style={{
										fontFamily: 'monospace'
									}}>
									{sourcePrefix}
									{codeSuffix}
								</strong>
							</div>
						)}
					</div>
					<Form.Item
						label={t('EventText')}
						name="text"
						rules={[{ required: true }]}>
						<Input.TextArea
							rows={2}
							placeholder={t('EventText') as string}
						/>
					</Form.Item>
					<Form.Item
						label={t('Type')}
						name="type"
						rules={[{ required: true }]}>
						<Select
							options={[
								{ label: t('error'), value: 'error' },
								{
									label: t('warning'),
									value: 'warning'
								},
								{ label: t('info'), value: 'info' },
								{
									label: t('success'),
									value: 'success'
								},
								{ label: t('app'), value: 'app' },
								{ label: t('debug'), value: 'debug' }
							]}
						/>
					</Form.Item>
					<Form.Item
						label={t('IsError')}
						name="isError"
						valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item
						label={t('Notify')}
						name="notify"
						valuePropName="checked">
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	)
}

function getTypeColor(type: string): string {
	const map: Record<string, string> = {
		error: 'red',
		warning: 'orange',
		info: 'blue',
		success: 'green',
		debug: 'purple',
		trace: 'cyan'
	}
	return map[type?.toLowerCase()] || 'default'
}

export default LoggingCodes
